import {
	priceWithPeriod,
	RR_TERMS,
	trialLengthLabel,
	vetoWindowLabel,
} from './rr-terms.ts'

export type ClusterSection = { h2: string; paras: string[] }

export type ClusterArticle = {
	slug: string
	title: string
	description: string
	h1: string
	lede: string
	sections: ClusterSection[]
	related: string[]
}

const AGENCY_RANGE = '$1,500 to $3,000 a month'
const AUTO_APPLY_RANGE = '$99 to $999 a year'
const APPLICATIONS_PER_MONTH = (RR_TERMS.applicationsPerWeek * 52) / 12
const PER_APPLICATION = `$${Math.round(
	RR_TERMS.priceUsd / APPLICATIONS_PER_MONTH,
)}`

export const CLUSTER: ClusterArticle[] = [
	{
		slug: 'what-is-a-reverse-recruiter',
		title: 'What Is a Reverse Recruiter?',
		description:
			'A recruiter is paid by the employer. A reverse recruiter is paid by you. What that changes, what they actually do, and when it is worth hiring one.',
		h1: 'What is a reverse recruiter?',
		lede: 'A recruiter is paid by a company to fill a job. A reverse recruiter is paid by you to find one.',
		sections: [
			{
				h2: 'The difference is who signs the cheque',
				paras: [
					'A company has a role to fill. It hires a recruiter. The recruiter finds the person. At no point in that arrangement are you the client — you are the thing being sourced. This is not a criticism of recruiters, it is just the shape of the deal. A recruiter who spends three hours improving your chances at a role they are not filling has done three hours of unpaid work.',
					'A reverse recruiter is hired by the job seeker. You pay them, so their only incentive is your outcome. They source roles that fit your experience rather than roles they happen to be filling, they position you for each one, and they do the application legwork that turns a job hunt into a second job.',
					'It is the same reason actors have agents. Someone whose only incentive is your outcome runs a different search than someone filling a requisition.',
				],
			},
			{
				h2: 'What a reverse recruiter actually does',
				paras: [
					'The work splits into four parts, and services differ mostly in how many of them they will do for you rather than advise you on.',
					'Sourcing: reading job postings — real ones, on company career pages and ATS platforms — and deciding which are worth your time. Positioning: rewriting your resume so it reads as a fit for a specific posting rather than a general one. Submission: filling out the application and filing it. Tracking: keeping a record of where your name went and what came back.',
					'A lot of services sell you the first two and leave you the last two, which are the parts that actually consume your evenings. Read the offer carefully. "We help you apply" and "we apply" are different products at similar prices.',
				],
			},
			{
				h2: 'You will see it called several things',
				paras: [
					'Reverse recruiting, reverse recruitment, reverse recruiter services, and increasingly AI reverse recruiter. The label moves around depending on who is selling it. The arrangement does not: whoever does the work, works for you.',
					`Here that means one person reads your resume, picks ${RR_TERMS.applicationsPerWeek} roles a week, rewrites your resume for each one, and files the application. Every role is emailed to you before it goes out, with the reason it was picked and where you fall short of the posting, and you have ${vetoWindowLabel} to stop it.`,
				],
			},
		],
		related: [
			'hire-someone-to-find-me-a-job',
			'how-reverse-recruiting-works',
			'reverse-recruiter-cost',
			'ai-reverse-recruiter-vs-traditional',
		],
	},
	{
		slug: 'how-reverse-recruiting-works',
		title: 'How Reverse Recruiting Works',
		description:
			'The weekly loop, step by step: intake, sourcing, announcement, tailoring, submission, receipts. What happens each week, and what you have to do.',
		h1: 'How reverse recruiting works',
		lede: 'The mechanics are the same wherever you buy it. Here is the loop, and it repeats every week.',
		sections: [
			{
				h2: 'Intake happens once',
				paras: [
					'You hand over a resume and say what you want: titles, industries, locations, a salary floor, and the things you will not compromise on. It takes about five minutes and you do it once. Everything after this is done for you, which is the entire proposition.',
					'A good intake asks awkward questions — the gap on your resume, the title that undersells you, the job you would rather not be judged by. A service that asks only for your resume and a job title is going to produce a search that could have been a saved search.',
				],
			},
			{
				h2: 'Sourcing, then a decision',
				paras: [
					'Someone searches company career pages, job boards and ATS platforms against what you asked for, reads the postings, and cuts to the handful worth applying to.',
					`The cut is the actual product. Anyone can return two hundred listings. Deciding that a few of them deserve your name, and being able to say why, is the work. Here that number is ${RR_TERMS.applicationsPerWeek} a week — and the roles that were read and passed on are shown to you too, with the reason each one did not make it.`,
				],
			},
			{
				h2: 'You are told before it goes out, not after',
				paras: [
					`Each role is emailed to you before it is filed, with the reason it was picked and an honest note about what the posting asks for that you do not have. You have ${vetoWindowLabel} to stop it with one click.`,
					'Silence means it ships. That sounds aggressive until you have tried the alternative: a service that waits for your approval stalls every time you have a busy week, which is every week when you are working full time and job hunting at night. Opt-out is what makes the thing run while you are at your day job.',
					'The stop window should be enforced rather than promised. In this product the submission code refuses to file an application while its window is still open — a server-side rule, not a habit somebody is trusted to keep.',
				],
			},
			{
				h2: 'Tailoring, submission, receipts',
				paras: [
					'Your resume is rewritten for that exact posting — keywords, ordering, emphasis — and the application is filled out and filed with that version attached.',
					'Then it is logged: which portal, the confirmation number, who filed it, and which version of your resume went. Receipts matter more than a dashboard. A status board tells you a number. A confirmation number tells you the application exists.',
				],
			},
		],
		related: [
			'what-is-a-reverse-recruiter',
			'is-reverse-recruiting-legit',
			'reverse-recruiting-vs-auto-apply',
			'reverse-recruiter-cost',
		],
	},
	{
		slug: 'ai-reverse-recruiter-vs-traditional',
		title: 'AI Reverse Recruiter vs. Traditional',
		description:
			'What "AI reverse recruiter" actually means, which part should stay human, and how to tell an AI-assisted service from software pretending to be one.',
		h1: 'AI reverse recruiter vs. traditional reverse recruiter',
		lede: 'The phrase "AI reverse recruiter" is doing a lot of work in this market, and it means at least three different things. Worth separating them before you pay for one.',
		sections: [
			{
				h2: 'Three things sold under one name',
				paras: [
					`The first is a traditional agency: a human career strategist, calls on their calendar, ${AGENCY_RANGE} on a retainer. It works. What you are buying is their hours, and hours are expensive.`,
					'The second is software that decides where your name goes. It scores listings against your resume, picks the top matches, and fires off applications. It is cheap, and it is the reason recruiters have stopped reading half their inbox.',
					'The third — the useful one — is a human doing the judgment with software underneath doing the typing. A person reads the posting and decides. The machine handles the resume mechanics.',
				],
			},
			{
				h2: 'Ask which part is automated',
				paras: [
					'Ask any service this directly: does a person read the posting before my name goes on the application? If the answer is a match score, the answer is no.',
					'A match percentage is a confidence number a model produced about a posting it did not read the way you would. It looks like evidence and it is not. We do not compute one, and there is no field for it in our database, because the moment a number like that exists it starts making decisions that ought to be somebody’s judgment.',
					'What replaces it is a sentence. Every role you are sent comes with the reason it was picked, written by the person who picked it, plus a note about what the posting asks for that you do not have. That second part is the one nobody volunteers. It is also the one that proves they read it.',
				],
			},
			{
				h2: 'Where the AI genuinely helps',
				paras: [
					'Rewriting a resume for a specific posting is real work and it is mostly mechanical: pull the language of the posting, reorder the experience, adjust the emphasis, keep it ATS-safe. Software is good at this, and it does not get tired on the fifth one of the week.',
					`So: the recruiting is human, and the resume engine is Resume Tailor — the same builder members get to use themselves. That split is why this costs ${priceWithPeriod} rather than agency money, and it is also why there is a hard cap of ${RR_TERMS.memberCap} members. One person can only read so many postings well.`,
				],
			},
		],
		related: [
			'find-my-profession-alternative',
			'reverse-recruiting-vs-auto-apply',
			'reverse-recruiter-cost',
			'what-is-a-reverse-recruiter',
		],
	},
	{
		slug: 'reverse-recruiting-vs-auto-apply',
		title: 'Reverse Recruiting vs. Auto-Apply Tools',
		description:
			'Auto-apply tools send one resume to hundreds of jobs. Why that fails, what reverse recruiting does instead, and how to tell which one you are buying.',
		h1: 'Reverse recruiting is not auto-apply',
		lede: 'There is a whole category of tool that blasts one generic resume at 500 jobs and calls it automation. It is easy to build. It does not work.',
		sections: [
			{
				h2: 'Why volume without fit fails',
				paras: [
					'Recruiters spot a generic application in seconds. The resume does not use the language of the posting, the experience is ordered for nobody in particular, and the same document is visibly in the pile three times for three different roles at the same company.',
					'The result is the pattern everyone recognises: hundreds of applications out, a handful of automated rejections back, and no information. You cannot learn anything from a failed mass campaign, because nothing in it was a test of anything.',
					'It is also worse than doing nothing in one specific way. Some ATS platforms deduplicate and flag repeat applicants, so a bad campaign can cost you the roles you would otherwise have had a shot at.',
				],
			},
			{
				h2: 'The other approach',
				paras: [
					`Reverse recruiting works the other way. Fewer jobs, chosen on purpose, each one read by a person before it is sent, each with a resume rewritten for that specific posting. ${RR_TERMS.applicationsPerWeek} a week.`,
					'The idea is that a small number of well-matched applications does more than a large number of generic ones. You can check whether it is working: you get the reasoning behind every job chosen, and a record of every application sent.',
				],
			},
			{
				h2: 'If you have already tried LazyApply or a tool like it',
				paras: [
					'You have probably seen the pattern: a lot of applications out, very few replies, and no way to tell which of them were ever a fit. That is not bad luck. The tool did what it was built to do, which is send applications, not choose them.',
					'Moving to a reverse recruiter means sending far fewer. What you get in exchange is a reason for each one, written by the person who chose it, and a note on where you fall short. After a few weeks that record tells you something a thousand automated applications never could: which kinds of roles answer you.',
				],
			},
			{
				h2: 'How to tell which one you are buying',
				paras: [
					'Three questions separate them cleanly.',
					'Does a human read the posting before applying? Does my resume change for each application, or is one master resume sent everywhere? Can I see what was submitted, where, and when — with a confirmation number?',
					`If a service cannot answer the third one, it is not tracking the applications it sent on your behalf, which means neither are you. Ask before you pay. And ask what happens in a slow week: here, ${RR_TERMS.applicationsPerWeek} hand-picked roles a week is the commitment, and if a week comes up short you tell us and we credit it.`,
				],
			},
		],
		related: [
			'website-that-applies-to-jobs-for-you',
			'how-reverse-recruiting-works',
			'ai-reverse-recruiter-vs-traditional',
			'reverse-recruiter-cost',
		],
	},
	{
		slug: 'reverse-recruiter-cost',
		title: 'How Much Does a Reverse Recruiter Cost?',
		description:
			'Reverse recruiting prices, from agency retainers to monthly services: what drives the spread, and how to work out whether it is worth it for you.',
		h1: 'How much does a reverse recruiter cost?',
		lede: 'Prices in this market range over two orders of magnitude, and the spread is mostly explained by how much of the work a human actually does.',
		sections: [
			{
				h2: 'The three price bands',
				paras: [
					`Full-service firms charge ${AGENCY_RANGE} on a retainer, and because their guarantees tend to require several consecutive months, one search runs $6,000 to $12,000. That price is honest: a person is spending real hours on your search, and hours are what you pay for. You usually also get coaching, interview prep and negotiation help bundled in.`,
					`Auto-apply tools cost ${AUTO_APPLY_RANGE} and send one generic resume to hundreds of listings. The price is honest here too — it reflects what is being done, which is very little.`,
					`In between sits the monthly service model, and that is where this product is: ${priceWithPeriod}, cancel anytime, with the first ${trialLengthLabel} free. A person does the recruiting, software does the resume mechanics, there is no retainer and no account manager, and one operator carries ${RR_TERMS.memberCap} members rather than a book of fifty.`,
				],
			},
			{
				h2: 'Work out what your own search costs',
				paras: [
					'A properly tailored application takes most people 45 to 90 minutes: read the posting, rewrite the resume, fill in the form, re-enter your work history because the parser mangled it.',
					'Apply to ten roles a month and that is eight to fifteen hours — a part-time job stacked on the job you are trying to leave. Reverse recruiting is worth it when your search costs you more than the service does, and the honest version of that sum includes the applications you did not send because you were tired.',
					'The other cost is duration. Every extra month of searching is a month of the salary you have not started earning. Against that, the monthly price of almost any of these services is a rounding error — which is an argument for buying one, though not for buying an expensive one.',
				],
			},
			{
				h2: 'What to check before you pay',
				paras: [
					'Whether the price is a retainer or a subscription you can cancel. Whether applications are actually submitted or only prepared. Whether you can see the receipts. Whether there is a cap on members, and whether anyone will tell you what it is.',
					`For what it is worth, ours is ${RR_TERMS.memberCap}, it is enforced at checkout rather than advertised loosely, and when it is full you go on a waitlist instead of being sold a seat that does not exist. The first ${trialLengthLabel} include a resume audit written by a person and up to ${RR_TERMS.freeApplications} applications actually filed, before your card is charged anything.`,
				],
			},
		],
		related: [
			'what-is-a-reverse-recruiter',
			'pay-someone-to-apply-to-jobs',
			'best-reverse-recruiting-services',
			'ai-reverse-recruiter-vs-traditional',
		],
	},
	{
		slug: 'hire-someone-to-find-me-a-job',
		title: 'Can You Hire Someone to Find You a Job?',
		description:
			'Recruiters work for employers, so most will tell you no. You can hire someone who works for you, though: what they do, what it costs, and what to ask first.',
		h1: 'Can you hire someone to find you a job?',
		lede: 'Yes, just not a recruiter. A recruiter is paid by the company with the opening, so you are what they are sourcing, not who they work for. The person you can hire is a reverse recruiter, and the only client they have is you.',
		sections: [
			{
				h2: 'Why a recruiter will usually say no',
				paras: [
					'Agency recruiters and staffing firms are paid by employers, usually as a fee when someone they put forward is hired. Ask one to find you a job and the honest ones will explain that they can only put you forward for roles they have been hired to fill. That is not a brush-off. It is the deal they are in.',
					'So searching for a way to hire a recruiter mostly turns up advice saying it cannot be done, and a few agencies hoping you will settle for a place in their database. Neither is what you asked for.',
				],
			},
			{
				h2: 'Who you can actually hire',
				paras: [
					'There are five kinds of help on the market, and they split cleanly by which part of the work they take off you.',
					'A career coach helps you decide what to go after and how to talk about yourself. They do not apply. A resume writer rewrites your resume once. They do not apply either. A virtual assistant or freelancer will submit applications, but only where you tell them to, so the judgment is still yours. Auto-apply software submits hundreds of applications without anybody reading the postings.',
					'A reverse recruiter is the only one of the five that does the whole job: reads the postings, decides which ones are worth your name, rewrites your resume for each, and files the application.',
				],
			},
			{
				h2: 'What it looks like when someone does it for you',
				paras: [
					`You do the intake once: your resume, the titles you want, where you will work, your salary floor and the things you will not take. After that a person reads postings against it and picks ${RR_TERMS.applicationsPerWeek} a week.`,
					`Each one is emailed to you before it goes out, with the reason it was picked and where you fall short of what the posting asks for. You have ${vetoWindowLabel} to stop it. If you say nothing, your resume is rewritten for that posting, the application is filed in your name, and you get the receipt: which portal, the confirmation number, and who filed it.`,
					'You also see the roles that were read and turned down, with the reason for each. That list is how you know somebody is actually reading.',
				],
			},
			{
				h2: 'What it costs',
				paras: [
					`Full-service firms charge ${AGENCY_RANGE} on a retainer, usually with coaching and interview prep bundled in. Here it is ${priceWithPeriod}, cancel anytime. The first ${trialLengthLabel} are free and include up to ${RR_TERMS.freeApplications} applications actually filed, and your first charge is refundable for ${RR_TERMS.refundDays} days.`,
					`That works out to about ${PER_APPLICATION} an application. It is a lot more than software charges and a lot less than an agency, and it pays for one thing: a person deciding where your name goes.`,
				],
			},
			{
				h2: 'What to ask before you hire anyone',
				paras: [
					'Who exactly is doing the work, and can you see their name? Will you see each role before it is submitted? Does your resume change for each posting? Will you get a confirmation number for every application?',
					'Then read the guarantee, if there is one. Nobody controls who an employer decides to interview, so any promise of interviews comes with conditions, often months of payments first. We do not guarantee interviews or a job. We guarantee the work: if a week comes up short, tell us and we credit it.',
				],
			},
			{
				h2: 'When this is the wrong thing to buy',
				paras: [
					'If applications are going out and no interviews are coming back, the problem is more likely your resume or your target than your volume, and a coach or a better resume will do more than another person applying.',
					'If you need visa sponsorship, live outside the US, or want federal, cleared or licensed roles, we are not set up for you yet, and we would rather say so before you pay.',
				],
			},
		],
		related: [
			'what-is-a-reverse-recruiter',
			'pay-someone-to-apply-to-jobs',
			'is-reverse-recruiting-legit',
		],
	},
	{
		slug: 'is-reverse-recruiting-legit',
		title: 'Is Reverse Recruiting Legit or a Scam?',
		description:
			'Some reverse recruiting services are real; some are a resume blast with a markup. The checks that tell them apart, and what no honest service can promise.',
		h1: 'Is reverse recruiting legit, or a scam?',
		lede: 'The idea is legitimate: you pay someone to do the searching and applying that a recruiter will never do for you. The category also attracts software with a human name on the box. Here is how to tell which one you are looking at.',
		sections: [
			{
				h2: 'Why people ask',
				paras: [
					'Most of the worry comes down to one thing: you pay, and you cannot see the work. Applications go somewhere, or nowhere, and the only evidence is a dashboard number the service controls. A legitimate service can show you each application. A bad one can only show you a count.',
					'The other worry is where your name ends up. A service that applies without asking can send you to a company you left on bad terms, a role two levels below you, or the same employer three times in a week.',
				],
			},
			{
				h2: 'Six checks that separate them',
				paras: [
					'A name. Who is doing the work? A real service will tell you, and will put it in writing.',
					'Proof for every application. You should get the portal, a confirmation number and a date for each one. A confirmation number is something you can check. A status bar is not.',
					'Seeing roles before they go. If you only find out where you applied afterwards, you have handed someone your name with no say in where it goes.',
					'A resume per posting. One resume sent everywhere is auto-apply, whatever the service calls it.',
					'A price you can walk away from. Monthly and cancelable is easy to test. A large non-refundable payment up front is a bet on the sales call.',
					'Who they turn away. A service that says it works for everyone is selling the subscription, not the search.',
				],
			},
			{
				h2: 'What no honest service can promise',
				paras: [
					'Interviews. Nobody controls who an employer decides to interview, so an interview guarantee always comes with conditions, often several consecutive months of fees before it applies. Read that fine print before you treat the guarantee as a reason to buy.',
					'What a service can guarantee is the work: how many applications, how they are chosen, and proof that each one happened.',
				],
			},
			{
				h2: 'How we answer those checks',
				paras: [
					'The work is done by one person, Brayan Londono, the founder. His name is in our Terms of Service.',
					`Every application is emailed to you before it is filed, with the reason it was picked and where you fall short of the posting. You have ${vetoWindowLabel} to stop it, and the software enforces that window: it refuses to file an application while the window is still open. Every application that goes out comes with its portal, confirmation number and who filed it. Roles that were read and turned down are listed too, with the reason.`,
					`The first ${trialLengthLabel} are free, including a resume audit written by a person within ${RR_TERMS.auditTurnaroundHours} hours and up to ${RR_TERMS.freeApplications} applications filed. Then it is ${priceWithPeriod}, cancel anytime, and the first charge is refundable for ${RR_TERMS.refundDays} days.`,
					'We turn people away too. If you are outside the US, need visa sponsorship, or are going for federal, cleared or licensed roles, we are not the right service yet.',
				],
			},
		],
		related: [
			'hire-someone-to-find-me-a-job',
			'how-reverse-recruiting-works',
			'reverse-recruiter-cost',
		],
	},
	{
		slug: 'pay-someone-to-apply-to-jobs',
		title: 'Paying Someone to Apply to Jobs for You',
		description:
			'Bots, freelancers, volume services and reverse recruiters all apply for you. What each costs per application, and what the extra money actually buys.',
		h1: 'Paying someone to apply to jobs for you',
		lede: 'Every option in this market will put your name on applications. The difference is whether anyone decides which ones, and whether your resume was written for the job it lands on.',
		sections: [
			{
				h2: 'The four ways to hand it off',
				paras: [
					`Auto-apply software is the cheapest, at roughly ${AUTO_APPLY_RANGE}. It fills in forms in bulk and nobody reads the postings.`,
					'A freelancer or virtual assistant will apply wherever you point them, usually by the hour. That saves the typing, but you still choose the jobs and you still tailor the resume, which are the two parts that take the time.',
					'Volume services promise hundreds of applications a month. Software usually finds the jobs; a person may check them or click submit.',
					'A reverse recruiter is a person who chooses the roles, rewrites your resume for each one and files the application. It is the only option that takes all of the work off you, and it sends far fewer applications because of it.',
				],
			},
			{
				h2: 'The math per application',
				paras: [
					`Here it is ${priceWithPeriod} for ${RR_TERMS.applicationsPerWeek} applications a week, roughly ${Math.round(APPLICATIONS_PER_MONTH)} a month, so about ${PER_APPLICATION} each. A full-service firm at $3,000 a month, aiming at five to ten roles a week, works out around $75 to $150 an application. Software costs pennies an application, because nobody's hours are involved.`,
					'Price per application is the wrong number to minimize, though. The number that matters is interviews per application, and that is mostly decided by two things software is bad at: whether the role is a real fit, and whether the resume reads like it was written for that posting.',
				],
			},
			{
				h2: `What the ${PER_APPLICATION} pays for`,
				paras: [
					'A person reading the posting and deciding it is worth your name, with the reason written down. A note on where you fall short of what it asks for, so nothing in the application overstates you. Your resume rewritten for that posting. The form filled in and filed in your name. A receipt with the portal and the confirmation number.',
					`And ${vetoWindowLabel} to stop any of it. Every role is emailed to you first, and if you say nothing it goes out, so the search keeps moving in the weeks you are too busy to look.`,
				],
			},
			{
				h2: 'When to keep doing it yourself',
				paras: [
					'If you only want a handful of specific companies, apply yourself. Nobody will care about those applications as much as you do. If you are sending applications and getting no interviews, paying for more of them will not fix it; fix the resume or the target first.',
					'Paying someone makes sense when you know what you want, the applications are the bottleneck, and the bottleneck is your evenings. If you would rather keep applying yourself, just faster, the Resume Tailor builder rewrites your resume for a specific posting in a few minutes.',
				],
			},
		],
		related: [
			'website-that-applies-to-jobs-for-you',
			'hire-someone-to-find-me-a-job',
			'reverse-recruiter-cost',
		],
	},
	{
		slug: 'website-that-applies-to-jobs-for-you',
		title: 'Websites That Apply to Jobs for You',
		description:
			'Auto-apply sites and extensions will send hundreds of applications for you. How they work, what they cost, where they backfire, and the alternative.',
		h1: 'Is there a website that applies to jobs for you?',
		lede: 'Yes, several. Most are browser extensions or bots that fill in application forms in bulk while you do something else. They are cheap and they do what they advertise. Whether that is what you need is a different question.',
		sections: [
			{
				h2: 'How auto-apply sites work',
				paras: [
					'You upload one resume, save answers to the common screening questions, and set filters for titles and locations. The tool searches job boards, opens each posting that matches, fills in the form with your saved answers and submits it. Many run as a browser extension inside your own logged-in accounts, and the bigger ones will send hundreds of applications a week.',
					`Pricing usually runs from ${AUTO_APPLY_RANGE}. For that you get volume and nothing else: nobody reads the postings, and the same resume goes to every one of them.`,
				],
			},
			{
				h2: 'Where they backfire',
				paras: [
					'One resume for every posting means none of them reads as a fit. Recruiters see generic applications all day and recognize them in seconds.',
					'Screening questions get answered from a template, so you can end up saying yes to a requirement you do not meet, or no to one you do. And applying to the same company several times in one week tells its recruiters exactly how the applications were made.',
					'A tool that drives your own logged-in account can also get that account restricted. LinkedIn, for one, lists bots and browser extensions that automate activity on the site as prohibited software.',
				],
			},
			{
				h2: 'When a bot is the right call',
				paras: [
					'If you are going for high-volume roles where speed matters more than fit, such as hourly or entry-level jobs that hire on a rolling basis, casting a wide net fast can be sensible. It is also a cheap way to find out which job titles get any response at all.',
					'It is the wrong tool when the roles are senior, specialized or scarce, because then each application has to look like it was meant for that job.',
				],
			},
			{
				h2: 'The other kind of "applies for you"',
				paras: [
					`A reverse recruiter makes the opposite trade: far fewer applications, and a person's judgment on every one. Here that is ${RR_TERMS.applicationsPerWeek} roles a week, picked by someone who read the posting, each emailed to you first with the reason and where you fall short, with ${vetoWindowLabel} to stop it.`,
					`Your resume is rewritten for each posting, the application is filed in your name, and you get the confirmation number. It costs ${priceWithPeriod} rather than ${AUTO_APPLY_RANGE}, and the first ${trialLengthLabel} are free.`,
				],
			},
			{
				h2: 'How to choose',
				paras: [
					'If your applications get interviews but you do not have time to send enough of them, hand the applying to a person. If they do not get interviews, more volume will not help, from a bot or from anyone; the resume or the target needs fixing first.',
					'And if you want to keep applying yourself, just faster, the Resume Tailor builder rewrites your resume for a specific posting in a few minutes.',
				],
			},
		],
		related: [
			'reverse-recruiting-vs-auto-apply',
			'pay-someone-to-apply-to-jobs',
			'ai-reverse-recruiter-vs-traditional',
		],
	},
	{
		slug: 'find-my-profession-alternative',
		title: `Find My Profession Alternative at $${RR_TERMS.priceUsd}/mo`,
		description: `Find My Profession charges $3,000 a month for human reverse recruiting. What that buys, what ${priceWithPeriod} gets you here, and which one fits your search.`,
		h1: 'A Find My Profession alternative',
		lede: 'Find My Profession is one of the best-known names in reverse recruiting, and it publishes its prices, which makes it easy to compare against. This comparison uses their own pages, checked in October 2026. We sell the alternative, so weigh it accordingly.',
		sections: [
			{
				h2: 'What Find My Profession charges',
				paras: [
					'Two ways to pay: $3,000 a month, or $1,500 a month plus 8% of your first-year salary, paid after you accept a job. The second option is for qualifying US searches. Payments are non-refundable, and billing renews every 30 days.',
					'Their interview guarantee asks for four consecutive months of service. If you have not had five qualifying interviews, no more than two from the same opening, or an accepted offer by then, they keep working for up to six more months at no charge. Four months at their prices comes to $6,000 to $12,000 before any success fee.',
				],
			},
			{
				h2: 'What that money buys',
				paras: [
					'A team of human recruiters. Each one carries five to seven clients at most and targets five to ten roles a week for each of them. You approve or decline every role within 48 hours, before anything is submitted.',
					'It is a serious service, priced like one, because people are spending real hours on your search.',
				],
			},
			{
				h2: 'What is the same here',
				paras: [
					`The shape is the same. A person reads the postings and picks the roles, your resume is rewritten for each one, the application is filed for you, and you see every role before it goes out. Here that is ${RR_TERMS.applicationsPerWeek} roles a week, each emailed to you with the reason it was picked.`,
				],
			},
			{
				h2: 'What is different',
				paras: [
					`The price: ${priceWithPeriod}, cancel anytime, with no retainer and no share of your salary. The first ${trialLengthLabel} are free with up to ${RR_TERMS.freeApplications} applications filed, and the first charge is refundable for ${RR_TERMS.refundDays} days.`,
					`The approval runs the other way. Find My Profession waits for you to approve each role. Here each role goes out unless you stop it within ${vetoWindowLabel}, so the search keeps moving in a week when you do not get to your inbox. If you would rather say yes to every one yourself, theirs is the better fit.`,
					'Every role comes with a note on where you fall short of what the posting asks for, and you can see the roles that were read and turned down, with the reason for each.',
					'One person does the work, not a team. There is no coaching, interview preparation, LinkedIn rewrite or salary negotiation, and no interview guarantee. We guarantee the work instead: if a week comes up short, tell us and we credit it.',
				],
			},
			{
				h2: 'Which one fits',
				paras: [
					'Find My Profession makes more sense if you want a team on your search, an interview guarantee matters to you, and $3,000 a month is small next to the role you are after.',
					`This makes more sense if you already know what you want, your resume is in decent shape, and what you are short of is the hours to apply properly. At ${priceWithPeriod} you can try it for a month without it becoming a bet.`,
				],
			},
		],
		related: [
			'best-reverse-recruiting-services',
			'reverse-recruiter-cost',
			'is-reverse-recruiting-legit',
		],
	},
	{
		slug: 'best-reverse-recruiting-services',
		title: 'Best Reverse Recruiting Services in 2026',
		description:
			'Reverse recruiting services compared on price, who picks the roles, whether you see them first, and what the guarantee really requires. We are one of them.',
		h1: 'The best reverse recruiting services, compared',
		lede: 'We sell one of these, so read this with that in mind. Every price and term below comes from each company’s own website, checked in October 2026, and the comparison includes the places where we come out worse.',
		sections: [
			{
				h2: 'Four questions that sort them',
				paras: [
					'Who picks the roles: a person, software, or you? Who submits the application? Do you see each role before your name goes on it? And what does the guarantee actually require before it pays out?',
					'Those four matter more than the price, because two services at the same price can be doing completely different work.',
				],
			},
			{
				h2: 'Human recruiters, billed monthly',
				paras: [
					'Find My Profession: $3,000 a month, or $1,500 a month plus 8% of first-year salary after you accept an offer. A team of recruiters, each with five to seven clients, aims at five to ten roles a week, and you approve each role within 48 hours before it is sent. The guarantee: five qualifying interviews or an offer within four consecutive months, or up to six more months free. Payments are non-refundable.',
					`Resume Tailor, which is us: ${priceWithPeriod}, with the first ${trialLengthLabel} free. One named person picks ${RR_TERMS.applicationsPerWeek} roles a week, rewrites your resume for each and files the application. Each role is emailed to you first with the reason it was picked and where you fall short, and goes out unless you stop it within ${vetoWindowLabel}. No interview guarantee; a short week is credited, and the first charge is refundable for ${RR_TERMS.refundDays} days.`,
				],
			},
			{
				h2: 'Volume services with people involved',
				paras: [
					'Pls Apply: $99 a month for 200 applications, or $199 for 400. A team picks roles from the targets you set at the start and says real people submit them. You see each application after it is sent. There is no guarantee of results, and payments are non-refundable once applications begin.',
					'ApplyAll: $299 one-time for 100 applications, or $349 for 200, at list price. Software ranks the jobs, people check the fit and fill in the forms over one to two weeks, and you give a blocklist up front rather than approving each role. It is the only service here with a money-back guarantee: a full refund if no qualifying first-round interview comes within 30 days of the applications being finished.',
					'Scale.jobs: one-time, from $199 for 250 applications to $1,099 for 1,100. By default you pick the jobs with their browser extension and assistants submit them, with a screenshot of each. A paid add-on has them find the jobs instead. Unused applications are refunded pro rata, minus a $100 fee.',
					'WeApply4U: one-time packages from $249 for 75 applications to $1,199 for 600. They recommend roles, you choose the target, and people submit each application with a timestamped receipt. You approve the resume and the target before it starts, not each role. All sales are final.',
				],
			},
			{
				h2: 'Resume services that also apply',
				paras: [
					'TopResume Get Hired Now: $2,495, or $3,995 for executives, as one fee. Job search specialists apply for you using an auto-apply feature. Its terms rule out refunds for not getting an interview or a job.',
				],
			},
			{
				h2: 'Software that applies',
				paras: [
					'LazyApply: $99, $149 or $999 a year. A Chrome extension that fills in applications in bulk, up to 1,500 a day on the top plan, with nobody reading the postings. Refunds apply only in narrow cases.',
					'Careery: from $500 one-time, or $750 for six months with a resume and LinkedIn rewrite. An AI filter picks the jobs and Careery applies as your agent. Sales are final.',
				],
			},
			{
				h2: 'Where we come out worse',
				paras: [
					`Volume. We send ${RR_TERMS.applicationsPerWeek} applications a week, a small fraction of what the volume services send. If your field rewards sheer numbers, they will out-send us.`,
					'Guarantees. ApplyAll refunds you if no interview comes, and Find My Profession keeps working for free. We do not guarantee interviews.',
					`Reach. One person does the work, so there is a cap of ${RR_TERMS.memberCap} members, and we only take US searches without visa sponsorship, and not federal, cleared or licensed roles.`,
				],
			},
			{
				h2: 'How to choose',
				paras: [
					'If you want a team and a guarantee, and budget is not the constraint, pay for a human firm. If your field hires on volume and you mainly need numbers out, a volume service or software costs less per application. If you want a person choosing every role and telling you why, at a monthly price you can walk away from, that is what we built.',
					'Whichever you pick, ask for a confirmation number for every application. It is the one thing that separates a service from a claim.',
				],
			},
		],
		related: [
			'find-my-profession-alternative',
			'is-reverse-recruiting-legit',
			'pay-someone-to-apply-to-jobs',
		],
	},
]

export const CLUSTER_SLUGS = CLUSTER.map(a => a.slug)

export function articleBySlug(slug: string): ClusterArticle | null {
	return CLUSTER.find(a => a.slug === slug) ?? null
}
